// frontend/src/pages/Proposals/PortalProposalDetail.jsx
// Vendor-facing: view a single proposal's status and linked agreement
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, Download, FileText } from "lucide-react";
import { getProposal, getProposalTemplateUrl } from "../../api/proposal.api.js";
import { Spinner, StatusBadge, formatDate } from "../../components/ui";

function InfoRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="px-5 py-4">
      <p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-0.5">{label}</p>
      <p className="text-sm text-gray-900">{value}</p>
    </div>
  );
}

export default function PortalProposalDetail() {
  const { id } = useParams();

  const { data: proposal, isLoading } = useQuery({
    queryKey: ["proposal", id],
    queryFn: () => getProposal(id),
  });

  const downloadTemplate = async () => {
    try {
      const { url } = await getProposalTemplateUrl(id);
      window.open(url, "_blank");
    } catch (err) {
      alert(err.message);
    }
  };

  if (isLoading) return <Spinner />;
  if (!proposal) return <p className="text-gray-500">Pengajuan tidak ditemukan.</p>;

  return (
    <div className="max-w-2xl mx-auto">
      <Link
        to="/portal/proposals"
        className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4"
      >
        <ChevronLeft size={15} /> Kembali ke Pengajuan
      </Link>

      <div className="flex items-start justify-between gap-4 mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{proposal.title}</h1>
        <StatusBadge status={proposal.status} />
      </div>

      <div className="card divide-y divide-gray-100">
        <InfoRow label="Tanggal Pengajuan" value={formatDate(proposal.createdAt)} />
        <InfoRow label="Maksud & Tujuan" value={proposal.purpose} />
        {proposal.description && <InfoRow label="Keterangan Tambahan" value={proposal.description} />}

        {proposal.ownTemplateFileName && (
          <div className="px-5 py-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-0.5">
                Template yang Dilampirkan
              </p>
              <p className="text-sm text-gray-900">{proposal.ownTemplateFileName}</p>
            </div>
            <button
              onClick={downloadTemplate}
              className="flex items-center gap-1.5 text-xs text-brand-600 hover:underline"
            >
              <Download size={13} /> Unduh
            </button>
          </div>
        )}

        {proposal.status === "REJECTED" && proposal.rejectionReason && (
          <div className="px-5 py-4 bg-red-50">
            <p className="text-xs text-red-600 uppercase tracking-wide font-medium mb-0.5">
              Alasan Penolakan
            </p>
            <p className="text-sm text-red-800">{proposal.rejectionReason}</p>
          </div>
        )}

        {proposal.createdAgreement && (
          <div className="px-5 py-4 flex items-center justify-between bg-green-50">
            <div>
              <p className="text-xs text-green-700 uppercase tracking-wide font-medium mb-0.5">
                Perjanjian Dibuat
              </p>
              <p className="text-sm text-green-900 font-medium">
                {proposal.createdAgreement.title}
              </p>
              <StatusBadge status={proposal.createdAgreement.status} />
            </div>
            <Link
              to={`/portal/agreements/${proposal.createdAgreement.id}`}
              className="flex items-center gap-1.5 text-sm text-green-700 hover:underline"
            >
              <FileText size={14} /> Lihat Perjanjian
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
